import React, { useState, useEffect, useCallback } from 'react'
import { tenantDataAPI } from '../services/api'
import {
  Sparkles,
  Key,
  Copy,
  RefreshCw,
  Trash2,
  Loader2,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
} from 'lucide-react'

/**
 * Super-admin AI Analysis panel for a tenant:
 * enablement (limits override) · model/effort/quota · single API key
 * (plaintext shown exactly once in a modal, masked afterwards) · usage metrics.
 */
const EFFORT_LABEL = { low: 'Baixo', medium: 'Médio', high: 'Alto' }

export const TenantAiPanel = ({ tenantId }) => {
  const [settings, setSettings] = useState(null)
  const [apiKey, setApiKey] = useState(null)
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [keyBusy, setKeyBusy] = useState(false)
  const [error, setError] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)
  const [form, setForm] = useState({ model_analysis: '', effort: 'medium', monthly_quota: 200 })
  const [newKeyModal, setNewKeyModal] = useState(null) // {api_key} while the show-once modal is open
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [settingsRes, keyRes, usageRes] = await Promise.all([
        tenantDataAPI.getAiSettings(tenantId),
        tenantDataAPI.getApiKey(tenantId),
        tenantDataAPI.getAiUsage(tenantId),
      ])
      const s = settingsRes.data.data
      setSettings(s)
      setForm({
        model_analysis: s.model_analysis,
        effort: s.effort,
        monthly_quota: s.monthly_quota,
      })
      setApiKey(keyRes.data.data)
      setUsage(usageRes.data.data)
    } catch {
      setError('Falha ao carregar as configurações de IA')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    load()
  }, [load])

  const handleToggleEnabled = async () => {
    setToggling(true)
    setError('')
    try {
      await tenantDataAPI.updateTenantLimits(tenantId, {
        analysis_enabled: !settings.analysis_enabled,
      })
      await load()
    } catch {
      setError('Falha ao atualizar a ativação da análise')
    } finally {
      setToggling(false)
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await tenantDataAPI.updateAiSettings(tenantId, {
        model_analysis: form.model_analysis,
        effort: form.effort,
        monthly_quota: parseInt(form.monthly_quota, 10),
      })
      setSettings((prev) => ({ ...prev, ...res.data.data }))
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2500)
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Falha ao salvar as configurações de IA')
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateKey = async () => {
    setKeyBusy(true)
    setError('')
    setConfirmRegenerate(false)
    try {
      const res = await tenantDataAPI.generateApiKey(tenantId)
      const data = res.data.data
      setNewKeyModal({ api_key: data.api_key })
      setApiKey({ ...data, api_key: undefined })
    } catch {
      setError('Falha ao gerar a chave de API')
    } finally {
      setKeyBusy(false)
    }
  }

  const handleDeleteKey = async () => {
    setKeyBusy(true)
    setError('')
    try {
      await tenantDataAPI.deleteApiKey(tenantId)
      setApiKey(null)
    } catch {
      setError('Falha ao excluir a chave de API')
    } finally {
      setKeyBusy(false)
    }
  }

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKeyModal.api_key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable — the key is still visible for manual copy */
    }
  }

  if (loading) {
    return (
      <div className="card p-6 flex items-center gap-2 text-admin-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando configurações de IA...
      </div>
    )
  }

  const quotaUsedPct =
    usage && usage.monthly_quota > 0
      ? Math.min(100, Math.round((usage.current_month_calls / usage.monthly_quota) * 100))
      : 0

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-admin-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Análise por IA
        </h2>
        <button
          onClick={handleToggleEnabled}
          disabled={toggling}
          className="flex items-center gap-2 text-sm font-medium"
          aria-label="Ativar ou desativar a análise por IA para este estúdio"
        >
          {settings?.analysis_enabled ? (
            <>
              <ToggleRight className="h-6 w-6 text-green-600" />
              <span className="text-green-700">Ativada</span>
            </>
          ) : (
            <>
              <ToggleLeft className="h-6 w-6 text-admin-400" />
              <span className="text-admin-500">Desativada</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-md p-3">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings */}
        <div>
          <h3 className="text-sm font-semibold text-admin-700 mb-3">Modelo e limites</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-admin-600 mb-1">Modelo de análise</label>
              <select
                className="input w-full"
                value={form.model_analysis}
                onChange={(e) => setForm({ ...form, model_analysis: e.target.value })}
              >
                {(settings?.allowed_models || []).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-admin-600 mb-1">Esforço</label>
              <select
                className="input w-full"
                value={form.effort}
                onChange={(e) => setForm({ ...form, effort: e.target.value })}
              >
                {(settings?.effort_levels || ['low', 'medium', 'high']).map((level) => (
                  <option key={level} value={level}>{EFFORT_LABEL[level] || level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-admin-600 mb-1">
                Cota mensal (chamadas, -1 = ilimitada)
              </label>
              <input
                type="number"
                min="-1"
                className="input w-full"
                value={form.monthly_quota}
                onChange={(e) => setForm({ ...form, monthly_quota: e.target.value })}
              />
            </div>
            <button onClick={handleSaveSettings} disabled={saving} className="btn btn-primary w-full">
              {saving ? 'Salvando...' : savedFlash ? 'Salvo ✓' : 'Salvar configurações'}
            </button>
          </div>
        </div>

        {/* API key */}
        <div>
          <h3 className="text-sm font-semibold text-admin-700 mb-3 flex items-center gap-1">
            <Key className="h-4 w-4" /> Chave de API
          </h3>
          {apiKey ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-admin-600 mb-1">Chave</label>
                <p className="font-mono text-sm text-admin-900">{apiKey.masked || `${apiKey.prefix}…****`}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-admin-600 mb-1">Criada em</label>
                <p className="text-sm text-admin-900">
                  {apiKey.created_at ? new Date(apiKey.created_at).toLocaleString('pt-BR') : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-admin-600 mb-1">Último uso</label>
                <p className="text-sm text-admin-900">
                  {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString('pt-BR') : 'Nunca'}
                </p>
              </div>
              {confirmRegenerate ? (
                <div className="rounded-md bg-amber-50 p-3 space-y-2">
                  <p className="text-sm text-amber-800">
                    Gerar uma nova chave invalida a atual imediatamente. Qualquer integração que a utilize
                    deixará de funcionar até ser atualizada.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={handleGenerateKey} disabled={keyBusy} className="btn btn-secondary text-amber-700">
                      {keyBusy ? 'Processando...' : 'Sim, gerar nova'}
                    </button>
                    <button onClick={() => setConfirmRegenerate(false)} className="btn btn-secondary">
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setConfirmRegenerate(true)} disabled={keyBusy} className="btn btn-secondary">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Gerar nova
                  </button>
                  <button
                    onClick={handleDeleteKey}
                    disabled={keyBusy}
                    className="btn btn-secondary text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-admin-600">
                Nenhuma chave de API. Gere uma para que as ferramentas externas do estúdio possam chamar a API de análise por IA.
              </p>
              <button onClick={handleGenerateKey} disabled={keyBusy} className="btn btn-primary">
                <Key className="h-4 w-4 mr-2" />
                {keyBusy ? 'Gerando...' : 'Gerar chave'}
              </button>
            </div>
          )}
        </div>

        {/* Usage */}
        <div>
          <h3 className="text-sm font-semibold text-admin-700 mb-3">Uso</h3>
          {usage && (
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-admin-600">Este mês</span>
                  <span className="text-admin-900 font-medium">
                    {usage.current_month_calls}
                    {usage.monthly_quota > 0 ? ` / ${usage.monthly_quota}` : ''} chamadas
                  </span>
                </div>
                {usage.monthly_quota > 0 && (
                  <div className="w-full bg-admin-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${quotaUsedPct >= 90 ? 'bg-red-500' : 'bg-purple-500'}`}
                      style={{ width: `${quotaUsedPct}%` }}
                    />
                  </div>
                )}
              </div>
              {usage.usage.length === 0 ? (
                <p className="text-sm text-admin-500">Nenhuma chamada de IA ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-admin-500">
                        <th className="py-1 pr-2 font-medium">Mês</th>
                        <th className="py-1 pr-2 font-medium">Tipo</th>
                        <th className="py-1 pr-2 font-medium text-right">Chamadas</th>
                        <th className="py-1 pr-2 font-medium text-right">Custo</th>
                        <th className="py-1 font-medium text-right">Erros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.usage.map((row) => (
                        <tr key={`${row.month}-${row.tipo}`} className="border-t border-admin-100">
                          <td className="py-1 pr-2 text-admin-900">{row.month}</td>
                          <td className="py-1 pr-2 text-admin-700">{row.tipo}</td>
                          <td className="py-1 pr-2 text-right text-admin-900">{row.calls}</td>
                          <td className="py-1 pr-2 text-right text-admin-900">${row.cost_usd.toFixed(2)}</td>
                          <td className="py-1 text-right text-admin-900">{row.errors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Show-once key modal */}
      {newKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-admin-900 mb-2 flex items-center gap-2">
              <Key className="h-5 w-5 text-purple-600" />
              Chave de API gerada
            </h3>
            <p className="text-sm text-admin-600 mb-4">
              Copie esta chave agora — <strong>ela não será exibida novamente</strong>. Guarde-a na
              configuração de segredos da integração.
            </p>
            <div className="flex items-center gap-2 bg-admin-50 rounded-md p-3 mb-4">
              <code className="font-mono text-sm break-all flex-1">{newKeyModal.api_key}</code>
              <button onClick={handleCopyKey} className="btn btn-secondary shrink-0" aria-label="Copiar chave de API">
                {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setNewKeyModal(null)} className="btn btn-primary">
                Já guardei a chave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
